import React from 'react';
import StatsHeatmap from './StatsHeatmap';
import { ChallengeType } from '../challenge-types';

interface DegreeStats {
  attempts: number;
  correct: number;
  totalTime: number;
  bestTime: number | null;
  worstTime: number | null;
}

interface KeyStats {
  [degree: number]: DegreeStats;
}

interface StatsSectionProps {
  stats: {
    accuracy: string;
    total: string;
    avgTime: string;
  };
  statsData: { [key: string]: KeyStats };
  modeStats: {
    forward: { accuracy: string; total: string; avgTime: string };
    inverse: { accuracy: string; total: string; avgTime: string };
    combined: { accuracy: string; total: string; avgTime: string };
  };
  modeStatsData: {
    forward: { [key: string]: KeyStats };
    inverse: { [key: string]: KeyStats };
  };
  selectedStatsMode: ChallengeType | 'combined';
  onResetStats: () => void;
  onResetStatsByMode: (mode: ChallengeType) => void;
  onStatsModeChange: (mode: ChallengeType | 'combined') => void;
}

const StatsSection: React.FC<StatsSectionProps> = ({ 
  stats, 
  statsData, 
  modeStats, 
  modeStatsData, 
  selectedStatsMode, 
  onResetStats, 
  onResetStatsByMode, 
  onStatsModeChange 
}) => {
  // Get current stats based on selected mode
  const currentStats = selectedStatsMode === 'combined' 
    ? modeStats.combined 
    : selectedStatsMode === ChallengeType.FORWARD 
      ? modeStats.forward 
      : modeStats.inverse;

  // Get current stats data for heatmap
  const currentStatsData = selectedStatsMode === 'combined' 
    ? statsData // Use legacy combined data
    : selectedStatsMode === ChallengeType.FORWARD 
      ? modeStatsData.forward 
      : modeStatsData.inverse;
  return (
    <section className="bg-white/95 backdrop-blur-sm rounded-xl p-3 sm:p-4 md:p-6 shadow-lg border border-white/20 space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header with Mode Selection and Reset Buttons */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <h2 className="text-base sm:text-lg font-medium text-slate-900">Statistics</h2>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
          {/* Mode Selection */}
          <div className="flex bg-slate-100 rounded-lg p-0.5 w-full sm:w-auto">
            <button
              onClick={() => onStatsModeChange(ChallengeType.FORWARD)}
              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-md transition-all ${
                selectedStatsMode === ChallengeType.FORWARD
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Forward
            </button>
            <button
              onClick={() => onStatsModeChange(ChallengeType.INVERSE)}
              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-md transition-all ${
                selectedStatsMode === ChallengeType.INVERSE
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Inverse
            </button>
            <button
              onClick={() => onStatsModeChange('combined')}
              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium rounded-md transition-all ${
                selectedStatsMode === 'combined'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              All
            </button>
          </div>

          {/* Reset Buttons */}
          <div className="flex gap-2 w-full sm:w-auto">
            {selectedStatsMode !== 'combined' && (
              <button
                onClick={() => onResetStatsByMode(selectedStatsMode as ChallengeType)}
                className="flex-1 sm:flex-none px-2 sm:px-3 py-2 bg-orange-600 text-white rounded-lg font-medium text-xs sm:text-sm
                           hover:bg-orange-700 transition-all shadow-md hover:shadow-lg active:scale-95 min-h-[36px]"
              >
                Reset {selectedStatsMode === ChallengeType.FORWARD ? 'Fwd' : 'Inv'}
              </button>
            )}
            <button
              data-testid="reset-stats-btn"
              onClick={onResetStats}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg font-medium text-xs sm:text-sm
                         hover:bg-red-700 transition-all shadow-md hover:shadow-lg active:scale-95 min-h-[36px]"
            >
              Reset All
            </button>
          </div>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 md:gap-4">
        <div className="text-center p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl bg-gradient-to-br from-slate-50 to-slate-100/50">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 sm:mb-2 font-medium">
            Accuracy
          </div>
          <div data-testid="overall-accuracy" className="text-lg sm:text-2xl md:text-3xl font-light text-slate-900">
            {currentStats.accuracy}
          </div>
        </div>
        
        <div className="text-center p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl bg-gradient-to-br from-slate-50 to-slate-100/50">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 sm:mb-2 font-medium">
            Total
          </div>
          <div data-testid="total-attempts" className="text-lg sm:text-2xl md:text-3xl font-light text-slate-900">
            {currentStats.total}
          </div>
        </div>
        
        <div className="text-center p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl bg-gradient-to-br from-slate-50 to-slate-100/50">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 sm:mb-2 font-medium">
            Avg Time
          </div>
          <div data-testid="avg-time" className="text-lg sm:text-2xl md:text-3xl font-light text-slate-900">
            {currentStats.avgTime}
          </div>
        </div>
      </div>

      {/* Detailed Heatmap */}
      <div className="pt-1 sm:pt-2">
        <StatsHeatmap statsData={currentStatsData} />
      </div>
    </section>
  );
};

export default StatsSection;